import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Loader2, Check, Copy, ExternalLink, Ban, ArrowRight, Search, User2,
} from 'lucide-react';
import { salesApi, formatMoney, formatMoneyShort, CURRENCIES, serverError } from '../../lib/salesApi';
import SaleQRCode from './SaleQRCode';

const CY = '#90E0EF';

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#777] mb-2">{label}</label>
      {children}
      {hint && <p className="text-[10px] font-mono text-[#555] mt-1.5">{hint}</p>}
    </div>
  );
}

const inputCls =
  'w-full bg-[#0d0d0d] border border-[#222] text-[#f0f0f0] text-sm font-mono px-3 py-2.5 rounded-none focus:outline-none focus:border-[#90E0EF] transition-colors placeholder:text-[#555]';

function Choice({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{ cursor: 'pointer', borderColor: active ? CY : '#222', color: active ? CY : '#aaa', background: active ? 'rgba(144,224,239,0.08)' : 'transparent' }}
      className="flex-1 border px-4 py-3 text-[11px] font-mono uppercase tracking-[0.16em] transition-all duration-200 hover:border-[#90E0EF]/50"
    >
      {children}
    </button>
  );
}

const STAGES = [
  { key: 'created', label: 'SALE CREATED' },
  { key: 'details', label: 'CUSTOMER DETAILS CONFIRMED' },
  { key: 'terms', label: 'TERMS ACCEPTED' },
  { key: 'started', label: 'PAYMENT STARTED' },
  { key: 'confirmed', label: 'PAYMENT CONFIRMED' },
];

function stageIndexForStatus(status) {
  if (status === 'paid') return 5;
  if (status === 'checkout_open') return 4;
  return 1; // pending
}

export default function NewSaleModal({ open, onClose, onCreated }) {
  const [phase, setPhase] = useState('form'); // form | created
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [seller, setSeller] = useState('relative_gallery');
  const [saleType, setSaleType] = useState('artwork');
  const [artists, setArtists] = useState([]);
  const [artistId, setArtistId] = useState('');
  const [artistName, setArtistName] = useState('');
  const [artistQuery, setArtistQuery] = useState('');
  const [items, setItems] = useState([]);
  const [itemTitle, setItemTitle] = useState('');
  const [edition, setEdition] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [existingClient, setExistingClient] = useState(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [notes, setNotes] = useState('');

  // created state
  const [sale, setSale] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (open) {
      resetAll();
      salesApi.artists().then(setArtists).catch(() => {});
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!artistId) { setItems([]); return; }
    salesApi.items(artistId).then(setItems).catch(() => setItems([]));
  }, [artistId]);

  function resetAll() {
    setPhase('form'); setSubmitting(false);
    setSeller('relative_gallery'); setSaleType('artwork');
    setArtistId(''); setArtistName(''); setArtistQuery(''); setItems([]);
    setItemTitle(''); setEdition(''); setCardNumber('');
    setBuyerName(''); setBuyerEmail(''); setExistingClient(null);
    setAmount(''); setCurrency('GBP'); setNotes('');
    setSale(null); setLiveStatus(null);
    clearInterval(pollRef.current);
  }

  const filteredArtists = useMemo(() => {
    const q = artistQuery.toLowerCase().trim();
    if (!q) return artists.slice(0, 8);
    return artists.filter((a) => (a.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [artists, artistQuery]);

  async function lookupClient() {
    const email = buyerEmail.trim();
    if (!email || !email.includes('@')) { setExistingClient(null); return; }
    try {
      const res = await salesApi.clientLookup(email);
      if (res.exists) {
        setExistingClient(res.client);
        if (!buyerName && res.client.name) setBuyerName(res.client.name);
      } else {
        setExistingClient(null);
      }
    } catch { setExistingClient(null); }
  }

  const amountMinor = useMemo(() => {
    const v = parseFloat((amount || '').toString().replace(',', '.'));
    if (isNaN(v) || v <= 0) return 0;
    return Math.round(v * 100);
  }, [amount]);

  // Match the backend's EmailStr expectation so a bad address is caught here
  // with a clear message instead of a cryptic 422 after submit.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const emailOk = EMAIL_RE.test(buyerEmail.trim());
  const emailInvalid = buyerEmail.trim().length > 0 && !emailOk;

  const canCreate =
    !!seller && !!saleType && !!itemTitle.trim() && emailOk && amountMinor >= 1;

  function pickItem(it) {
    setItemTitle(it.title || '');
    setEdition(it.edition || '');
    if (it.amount_minor != null) setAmount((it.amount_minor / 100).toString());
    if (it.currency) setCurrency(it.currency);
  }

  function pickArtist(a) {
    setArtistId(a.id); setArtistName(a.name || ''); setArtistQuery(a.name || '');
  }

  async function submit() {
    if (!canCreate) return;
    setSubmitting(true);
    try {
      const body = {
        seller_scope: seller,
        sale_type: saleType,
        artist_id: artistId || null,
        artist_name: artistName || artistQuery || null,
        item_title: itemTitle.trim(),
        edition: edition.trim() || null,
        card_number: saleType === 'card' && cardNumber ? parseInt(cardNumber, 10) : null,
        buyer_name: buyerName.trim() || null,
        buyer_email: buyerEmail.trim(),
        amount_minor: amountMinor,
        currency,
        notes: notes.trim() || null,
      };
      const res = await salesApi.create(body);
      setSale(res.sale);
      setLiveStatus(res.sale.status);
      setPhase('created');
      onCreated?.();
      startPolling(res.sale.public_code);
    } catch (err) {
      toast.error(serverError(err, 'Could not create the sale.'));
    } finally {
      setSubmitting(false);
    }
  }

  function startPolling(code) {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await salesApi.publicStatus(code);
        setLiveStatus(s.status);
        if (s.status === 'paid') {
          clearInterval(pollRef.current);
          onCreated?.();
        }
      } catch { /* keep polling */ }
    }, 3000);
  }

  const paymentUrl = sale ? `${window.location.origin}/p/${sale.public_code}` : '';
  const stageIdx = stageIndexForStatus(liveStatus);

  async function cancelThis() {
    if (!sale) return;
    try {
      await salesApi.cancel(sale.id);
      clearInterval(pollRef.current);
      toast.success('Sale cancelled.');
      onCreated?.();
      onClose();
    } catch (err) { toast.error(serverError(err)); }
  }

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] bg-[#050505] admin-root overflow-y-auto"
        data-testid="new-sale-modal"
      >
        {/* grid backdrop */}
        <div className="pointer-events-none fixed inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

        <header className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-10 h-16 border-b border-[#161616] bg-[#050505]/90 backdrop-blur">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 10px ${CY}` }} />
            <span className="font-outfit font-semibold text-white tracking-tight text-lg">
              {phase === 'form' ? 'New Sale' : 'Sale Created'}
            </span>
            <span className="text-[10px] font-mono text-[#555] tracking-[0.2em] uppercase hidden sm:block">Transaction Terminal</span>
          </div>
          <button onClick={onClose} data-testid="new-sale-close" style={{ cursor: 'pointer' }} className="text-[#888] hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </header>

        {phase === 'form' && (
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 relative">
            {/* LEFT — form */}
            <div className="space-y-8">
              <Field label="01 · Seller">
                <div className="flex gap-3">
                  <Choice active={seller === 'mr_relative'} onClick={() => setSeller('mr_relative')} testid="seller-mr-relative">Mr. Relative</Choice>
                  <Choice active={seller === 'relative_gallery'} onClick={() => setSeller('relative_gallery')} testid="seller-relative-gallery">The Relative Gallery</Choice>
                </div>
              </Field>

              <Field label="02 · Sale Type">
                <div className="flex gap-3">
                  <Choice active={saleType === 'artwork'} onClick={() => setSaleType('artwork')} testid="type-artwork">Artwork</Choice>
                  <Choice active={saleType === 'card'} onClick={() => setSaleType('card')} testid="type-card">Card</Choice>
                  <Choice active={saleType === 'other'} onClick={() => setSaleType('other')} testid="type-other">Other</Choice>
                </div>
              </Field>

              <Field label="03 · Artist" hint="Search existing artists, or just type a name.">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-[#555]" />
                  <input
                    className={inputCls + ' pl-9'}
                    placeholder="Artist name"
                    value={artistQuery}
                    onChange={(e) => { setArtistQuery(e.target.value); setArtistId(''); setArtistName(e.target.value); }}
                    data-testid="artist-search"
                  />
                </div>
                {artistQuery && filteredArtists.length > 0 && !artistId && (
                  <div className="mt-1 border border-[#1c1c1c] bg-[#0d0d0d] divide-y divide-[#161616]">
                    {filteredArtists.map((a) => (
                      <button key={a.id} type="button" onClick={() => pickArtist(a)} style={{ cursor: 'pointer' }}
                        className="w-full text-left px-3 py-2 text-xs font-mono text-[#ccc] hover:bg-[#141414] hover:text-white flex items-center gap-2">
                        <User2 className="w-3.5 h-3.5 text-[#555]" /> {a.name}
                      </button>
                    ))}
                  </div>
                )}
                {artistId && <p className="mt-1.5 text-[10px] font-mono" style={{ color: CY }}>✓ Linked to existing artist</p>}
              </Field>

              <Field label="04 · Item">
                {saleType === 'card' && (
                  <div className="mb-3">
                    <input className={inputCls} placeholder="Card number (e.g. 64)" value={cardNumber} inputMode="numeric"
                      onChange={(e) => setCardNumber(e.target.value.replace(/[^0-9]/g, ''))} data-testid="card-number-input" />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
                  <input className={inputCls} placeholder="Title (e.g. Black Hole)" value={itemTitle} onChange={(e) => setItemTitle(e.target.value)} data-testid="item-title-input" />
                  <input className={inputCls} placeholder="Edition (e.g. 1/5)" value={edition} onChange={(e) => setEdition(e.target.value)} data-testid="item-edition-input" />
                </div>
                {items.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {items.slice(0, 10).map((it) => (
                      <button key={`${it.source}-${it.id}`} type="button" onClick={() => pickItem(it)} style={{ cursor: 'pointer' }}
                        className="text-[10px] font-mono px-2.5 py-1.5 border border-[#242424] text-[#aaa] hover:border-[#90E0EF]/50 hover:text-white transition-colors">
                        {it.title}{it.edition ? ` · ${it.edition}` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </Field>

              <Field label="05 · Client">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input className={inputCls} placeholder="Name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} data-testid="buyer-name-input" />
                  <input className={inputCls} placeholder="Email" type="email" value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)} onBlur={() => { if (emailOk) lookupClient(); }} data-testid="buyer-email-input" />
                </div>
                {emailInvalid && (
                  <p className="mt-2 text-[10px] font-mono tracking-[0.1em]" style={{ color: '#e88ba0' }} data-testid="buyer-email-error">
                    Enter a valid email address (e.g. name@domain.com).
                  </p>
                )}
                {existingClient && (
                  <div className="mt-2 border border-[#2a3a3f] bg-[rgba(144,224,239,0.06)] px-3 py-2 flex items-center justify-between" data-testid="existing-client-badge">
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em]" style={{ color: CY }}>Existing Client</span>
                    <span className="text-[10px] font-mono text-[#bbb]">
                      {existingClient.purchase_count} purchases · {formatMoney(existingClient.total_purchased_minor, existingClient.default_currency)} total
                    </span>
                  </div>
                )}
              </Field>

              <Field label="06 · Price">
                <div className="grid grid-cols-[1fr_120px] gap-3">
                  <input className={inputCls} placeholder="0.00" inputMode="decimal" value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))} data-testid="amount-input" />
                  <select className={inputCls} value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ cursor: 'pointer' }} data-testid="currency-select">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </Field>

              <Field label="Notes (internal, optional)">
                <textarea className={inputCls + ' resize-none'} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sale-notes-input" />
              </Field>
            </div>

            {/* RIGHT — live summary */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="border border-[#1a1a1a] bg-[#080808] p-6">
                <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666] mb-4">Summary</p>
                <div className="space-y-3 text-xs font-mono">
                  <Row k="Seller" v={seller === 'mr_relative' ? 'MR. RELATIVE' : 'THE RELATIVE GALLERY'} />
                  <Row k="Type" v={saleType.toUpperCase()} />
                  <Row k="Artist" v={artistName || artistQuery || '—'} />
                  <Row k="Item" v={itemTitle || '—'} />
                  <Row k="Edition" v={edition || '—'} />
                  <Row k="Client" v={buyerName || buyerEmail || '—'} />
                </div>
                <div className="mt-6 pt-5 border-t border-[#1a1a1a]">
                  <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#666]">Total</p>
                  <p className="font-outfit font-bold text-white mt-1" style={{ fontSize: '2.4rem', lineHeight: 1 }} data-testid="summary-total">
                    {amountMinor ? formatMoney(amountMinor, currency) : '—'}
                  </p>
                </div>
                <button
                  onClick={submit}
                  disabled={!canCreate || submitting}
                  data-testid="create-sale-btn"
                  style={{ cursor: canCreate && !submitting ? 'pointer' : 'not-allowed', background: canCreate ? CY : 'transparent', border: `1px solid ${canCreate ? CY : '#2a2a2a'}` }}
                  className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 text-[11px] font-mono uppercase tracking-[0.2em] transition-all duration-200 disabled:opacity-40"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" style={{ color: '#050505' }} /> <span style={{ color: '#050505' }}>Creating…</span></>
                    : <><span style={{ color: canCreate ? '#050505' : '#888' }}>Create Sale</span><ArrowRight className="w-4 h-4" style={{ color: canCreate ? '#050505' : '#888' }} /></>}
                </button>
              </div>
            </div>
          </div>
        )}

        {phase === 'created' && sale && (
          <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 relative">
            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-10 items-start">
              {/* QR */}
              <div className="flex flex-col items-center">
                <div className="border border-[#1c1c1c] p-3 bg-[#050505]">
                  <SaleQRCode value={paymentUrl} size={200} />
                </div>
                <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-[#666]">Public code</p>
                <p className="font-mono tracking-[0.3em] text-2xl mt-1" style={{ color: CY }} data-testid="created-public-code">{sale.public_code}</p>
                <p className="mt-3 text-[10px] font-mono text-[#555] break-all text-center">{paymentUrl}</p>
              </div>

              {/* details */}
              <div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h2 className="font-outfit font-bold text-white text-3xl tracking-tight">{sale.item_title}</h2>
                  {sale.edition && <span className="text-sm font-mono text-[#888]">{sale.edition}</span>}
                </div>
                <p className="mt-1 text-sm font-mono text-[#aaa]">{sale.artist_name || '—'}</p>
                <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-6 text-xs font-mono">
                  <Row k="Seller" v={sale.seller_label} />
                  <Row k="Buyer" v={sale.buyer_name || sale.buyer_email} />
                </div>
                <p className="mt-6 font-outfit font-bold text-white" style={{ fontSize: '2.8rem', lineHeight: 1 }} data-testid="created-price">
                  {formatMoney(sale.amount_minor, sale.currency)}
                </p>

                {/* stages */}
                <div className="mt-8 space-y-2.5" data-testid="sale-stages">
                  {STAGES.map((st, i) => {
                    const done = i < stageIdx;
                    const isConfirmed = st.key === 'confirmed' && liveStatus === 'paid';
                    return (
                      <div key={st.key} className="flex items-center gap-3">
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] shrink-0"
                          style={{ border: `1px solid ${done ? CY : '#333'}`, background: done ? (isConfirmed ? CY : 'rgba(144,224,239,0.15)') : 'transparent' }}>
                          {done && <Check className="w-2.5 h-2.5" style={{ color: isConfirmed ? '#050505' : CY }} />}
                        </span>
                        <span className="text-[11px] font-mono uppercase tracking-[0.14em]" style={{ color: done ? '#e8e8e8' : '#555' }}>{st.label}</span>
                      </div>
                    );
                  })}
                </div>

                {liveStatus === 'paid' && (
                  <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="mt-6 inline-flex items-center gap-2 border px-4 py-2" style={{ borderColor: CY, color: CY, background: 'rgba(144,224,239,0.08)' }} data-testid="paid-confirmed">
                    <Check className="w-4 h-4" /> <span className="text-[11px] font-mono uppercase tracking-[0.2em]">Payment Confirmed · {formatMoneyShort(sale.amount_minor, sale.currency)}</span>
                  </motion.div>
                )}

                {/* actions */}
                <div className="mt-8 flex flex-wrap gap-3">
                  <button onClick={() => { navigator.clipboard.writeText(paymentUrl); toast.success('Link copied'); }} style={{ cursor: 'pointer' }}
                    data-testid="copy-link-btn" className="inline-flex items-center gap-2 border border-[#2a2a2a] text-[#ccc] hover:text-white hover:border-[#90E0EF]/50 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] transition-colors">
                    <Copy className="w-3.5 h-3.5" /> Copy Link
                  </button>
                  <a href={paymentUrl} target="_blank" rel="noreferrer" data-testid="open-checkout-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] transition-colors" style={{ background: CY, color: '#050505' }}>
                    <ExternalLink className="w-3.5 h-3.5" /> Open Checkout
                  </a>
                  {liveStatus !== 'paid' && (
                    <button onClick={cancelThis} style={{ cursor: 'pointer' }} data-testid="cancel-sale-btn"
                      className="inline-flex items-center gap-2 border border-[#3a2020] text-[#e88ba0] hover:bg-[#2a1515] px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] transition-colors">
                      <Ban className="w-3.5 h-3.5" /> Cancel Sale
                    </button>
                  )}
                </div>

                <div className="mt-8">
                  <button onClick={onClose} style={{ cursor: 'pointer' }} className="text-[11px] font-mono text-[#666] hover:text-white transition-colors">← Back to Sales</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[#666]">{k}</span>
      <span className="text-[#e8e8e8] text-right truncate">{v}</span>
    </div>
  );
}
